import React from 'react';
import { useWatch } from 'react-hook-form';
import { useCountries, useRegions } from 'modules/geo';
import { Control } from 'react-hook-form/dist/types/form';
import { Divider, Stack, Typography } from '@mui/material';
import { InputField, SelectField, CheckboxField } from 'components/form/rhf';
import { LocationOnOutlined, AccountCircleOutlined } from '@mui/icons-material';

import { AccountContactFormValues } from './accountContactFormSchema';

interface Props {
  control: Control<AccountContactFormValues>;
}

export const AccountContactFormFields = ({ control }: Props) => {
  const contactCountry = useWatch({ control, name: 'contactAddress.country' });
  const deliveryCountry = useWatch({ control, name: 'deliveryAddress.country' });
  const sameAsContact = useWatch({ control, name: 'sameAsContact' });

  const { countries } = useCountries();
  const { regions: contactRegions } = useRegions(contactCountry ?? 'US');
  const { regions: deliveryRegions } = useRegions(deliveryCountry ?? 'US');

  const isContactUS = contactCountry === 'US';
  const isDeliveryUS = deliveryCountry === 'US';

  return (
    <>
      <Stack gap="0.75rem">
        <Stack direction="row" gap="0.75rem" alignItems="center">
          <AccountCircleOutlined />

          <Typography variant="subtitle2" component="span">
            Contact Information
          </Typography>
        </Stack>

        <Stack direction="row" gap="1rem">
          <InputField
            label="First Name"
            name="firstName"
            control={control}
            inputProps={{
              required: true,
              variant: 'outlined',
            }}
          />

          <InputField
            label="Last Name"
            name="lastName"
            control={control}
            inputProps={{
              required: true,
              variant: 'outlined',
            }}
          />
        </Stack>

        <Stack direction="row" gap="1rem">
          <InputField
            name="phone"
            label="Phone"
            control={control}
            inputProps={{
              type: 'tel',
              required: true,
              variant: 'outlined',
              mask: '(999) 999-9999',
            }}
          />

          <InputField
            name="email"
            label="Email"
            control={control}
            inputProps={{
              variant: 'outlined',
            }}
          />
        </Stack>

        <InputField
          name="title"
          label="Title"
          control={control}
          inputProps={{
            variant: 'outlined',
          }}
        />
      </Stack>

      <Divider />

      <Stack gap="0.75rem">
        <Stack direction="row" gap="0.75rem" alignItems="center">
          <LocationOnOutlined />

          <Typography variant="subtitle2" component="span">
            Contact Address
          </Typography>
        </Stack>

        <SelectField
          label="Country"
          control={control}
          variant="outlined"
          options={countries}
          name="contactAddress.country"
        />

        <Stack direction="row" gap="1rem">
          <InputField
            control={control}
            label="Street Address"
            name="contactAddress.streetAddress"
            inputProps={{
              required: true,
              variant: 'outlined',
            }}
          />
          <InputField
            label="City"
            control={control}
            name="contactAddress.city"
            inputProps={{
              required: true,
              variant: 'outlined',
            }}
          />
          <SelectField
            required
            control={control}
            variant="outlined"
            options={contactRegions}
            name="contactAddress.state"
            label={isContactUS ? 'State' : 'Province'}
          />
        </Stack>

        <InputField
          control={control}
          name="contactAddress.zip"
          label={isContactUS ? 'ZIP Code' : 'Postal Code'}
          inputProps={{
            required: true,
            variant: 'outlined',
          }}
        />
      </Stack>

      <Divider />

      <Stack gap="0.75rem">
        <Stack direction="row" gap="0.75rem" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap="0.75rem" alignItems="center">
            <LocationOnOutlined />

            <Typography variant="subtitle2" component="span">
              Delivery Address
            </Typography>
          </Stack>
          <CheckboxField control={control} name="sameAsContact" label="Same as Contact Address" />
        </Stack>

        {!sameAsContact && (
          <>
            <SelectField
              label="Country"
              control={control}
              variant="outlined"
              options={countries}
              name="deliveryAddress.country"
            />

            <Stack direction="row" gap="1rem">
              <InputField
                control={control}
                label="Street Address"
                name="deliveryAddress.streetAddress"
                inputProps={{
                  required: true,
                  variant: 'outlined',
                }}
              />

              <InputField
                label="City"
                control={control}
                name="deliveryAddress.city"
                inputProps={{
                  required: true,
                  variant: 'outlined',
                }}
              />

              <SelectField
                required
                variant="outlined"
                control={control}
                options={deliveryRegions}
                name="deliveryAddress.state"
                label={isDeliveryUS ? 'State' : 'Province'}
              />
            </Stack>

            <InputField
              control={control}
              name="deliveryAddress.zip"
              label={isDeliveryUS ? 'ZIP Code' : 'Postal Code'}
              inputProps={{
                required: true,
                variant: 'outlined',
              }}
            />
          </>
        )}
      </Stack>
    </>
  );
};
